import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';
import { MapPin, Phone, Mail, Clock, Save, Star, Users, Car, Calendar } from 'lucide-react';
import { Input } from '@/components/ui/input';

const SiteSettingsManagement = () => {
  const [settings, setSettings] = useState({
    map_lat: 30.1732,
    map_lng: -97.7711,
    map_zoom: 15,
    google_rating: 4.8,
    google_review_count: 47,
    phone: '(512) 666-0016',
    email: 'info@wehaveyourcar.com',
    address: '8203 Bluff Springs Rd, Unit 4',
    city: 'Austin',
    state: 'TX',
    zip: '78744',
    business_hours: 'Mon-Sat: 10AM - 6PM',
    // Homepage stats
    stat_happy_customers: '500+',
    stat_vehicles_sold: '100+',
    stat_years_in_business: '5'
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/site-settings`);
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('adminToken');
      await axios.put(`${API}/admin/site-settings`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Settings saved!');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return <div className="text-white">Loading settings...</div>;
  }

  return (
    <div data-testid="site-settings-management">
      <div className="flex flex-wrap justify-between items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Site Settings</h1>
          <p className="text-[#a8b8d0] text-sm mt-1">Manage business information and map location</p>
        </div>
        <button onClick={handleSave} disabled={saving}
          className="btn-accent inline-flex items-center gap-2 disabled:opacity-50">
          <Save size={18} />
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Business Info */}
        <div className="bg-[#132238] rounded-xl p-6 border border-[#1a2d47]">
          <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
            <Phone size={20} className="text-[#ff6b35]" />
            Business Information
          </h2>
          <div className="space-y-4">
            <div>
              <label className="text-[#a8b8d0] text-sm mb-1 block">Phone Number</label>
              <Input value={settings.phone}
                onChange={(e) => handleChange('phone', e.target.value)}
                className="bg-[#1a2d47] border-[#1a4d8c] text-white" />
            </div>
            <div>
              <label className="text-[#a8b8d0] text-sm mb-1 block">Email</label>
              <Input value={settings.email}
                onChange={(e) => handleChange('email', e.target.value)}
                className="bg-[#1a2d47] border-[#1a4d8c] text-white" />
            </div>
            <div>
              <label className="text-[#a8b8d0] text-sm mb-1 block">Street Address</label>
              <Input value={settings.address}
                onChange={(e) => handleChange('address', e.target.value)}
                className="bg-[#1a2d47] border-[#1a4d8c] text-white" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="text-[#a8b8d0] text-sm mb-1 block">City</label>
                <Input value={settings.city}
                  onChange={(e) => handleChange('city', e.target.value)}
                  className="bg-[#1a2d47] border-[#1a4d8c] text-white" />
              </div>
              <div>
                <label className="text-[#a8b8d0] text-sm mb-1 block">State</label>
                <Input value={settings.state}
                  onChange={(e) => handleChange('state', e.target.value)}
                  className="bg-[#1a2d47] border-[#1a4d8c] text-white" />
              </div>
              <div>
                <label className="text-[#a8b8d0] text-sm mb-1 block">ZIP</label>
                <Input value={settings.zip}
                  onChange={(e) => handleChange('zip', e.target.value)}
                  className="bg-[#1a2d47] border-[#1a4d8c] text-white" />
              </div>
            </div>
            <div>
              <label className="text-[#a8b8d0] text-sm mb-1 block">Business Hours</label>
              <Input value={settings.business_hours}
                onChange={(e) => handleChange('business_hours', e.target.value)}
                placeholder="Mon-Sat: 10AM - 6PM"
                className="bg-[#1a2d47] border-[#1a4d8c] text-white" />
            </div>
          </div>
        </div>

        {/* Map Settings */}
        <div className="bg-[#132238] rounded-xl p-6 border border-[#1a2d47]">
          <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
            <MapPin size={20} className="text-[#ff6b35]" />
            Map Pin Location
          </h2>
          <div className="space-y-4">
            <p className="text-[#a8b8d0] text-sm">
              Adjust the coordinates to move the map pin on the Contact page.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[#a8b8d0] text-sm mb-1 block">Latitude</label>
                <Input type="number" step="0.0001" value={settings.map_lat}
                  onChange={(e) => handleChange('map_lat', parseFloat(e.target.value))}
                  className="bg-[#1a2d47] border-[#1a4d8c] text-white" />
              </div>
              <div>
                <label className="text-[#a8b8d0] text-sm mb-1 block">Longitude</label>
                <Input type="number" step="0.0001" value={settings.map_lng}
                  onChange={(e) => handleChange('map_lng', parseFloat(e.target.value))}
                  className="bg-[#1a2d47] border-[#1a4d8c] text-white" />
              </div>
            </div>
            <div>
              <label className="text-[#a8b8d0] text-sm mb-1 block">Zoom Level (1-20)</label>
              <Input type="number" min="1" max="20" value={settings.map_zoom}
                onChange={(e) => handleChange('map_zoom', parseInt(e.target.value))}
                className="bg-[#1a2d47] border-[#1a4d8c] text-white" />
            </div>
            
            {/* Map Preview */}
            <div className="mt-4 rounded-lg overflow-hidden border border-[#1a4d8c]">
              <iframe
                title="Map Preview"
                width="100%"
                height="200"
                frameBorder="0"
                src={`https://www.openstreetmap.org/export/embed.html?bbox=${settings.map_lng - 0.01},${settings.map_lat - 0.01},${settings.map_lng + 0.01},${settings.map_lat + 0.01}&layer=mapnik&marker=${settings.map_lat},${settings.map_lng}`}
                className="grayscale"
              />
            </div>
          </div>
        </div>

        {/* Google Rating Display */}
        <div className="bg-[#132238] rounded-xl p-6 border border-[#1a2d47]">
          <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
            <Star size={20} className="text-[#ffc107]" />
            Google Rating Display
          </h2>
          <p className="text-[#a8b8d0] text-sm mb-4">
            These values are displayed on the homepage. Update them to match your actual Google rating.
          </p>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-[#a8b8d0] text-sm mb-1 block">Star Rating (1-5)</label>
              <Input type="number" min="1" max="5" step="0.1" value={settings.google_rating}
                onChange={(e) => handleChange('google_rating', parseFloat(e.target.value))}
                className="bg-[#1a2d47] border-[#1a4d8c] text-white" />
            </div>
            <div>
              <label className="text-[#a8b8d0] text-sm mb-1 block">Review Count</label>
              <Input type="number" min="0" value={settings.google_review_count}
                onChange={(e) => handleChange('google_review_count', parseInt(e.target.value))}
                className="bg-[#1a2d47] border-[#1a4d8c] text-white" />
            </div>
          </div>
          <div className="mt-4 bg-[#1a2d47]/50 rounded-lg p-4">
            <p className="text-[#a8b8d0] text-sm mb-2">Preview:</p>
            <div className="flex items-center gap-2">
              <div className="flex">
                {[1,2,3,4,5].map(i => (
                  <Star key={i} size={16} 
                    className={i <= Math.round(settings.google_rating) ? 'text-[#ffc107] fill-[#ffc107]' : 'text-gray-500'} />
                ))}
              </div>
              <span className="text-white font-medium">{settings.google_rating}</span>
              <span className="text-[#a8b8d0]">({settings.google_review_count} reviews)</span>
            </div>
          </div>
        </div>

        {/* Homepage Stats */}
        <div className="bg-[#132238] rounded-xl p-6 border border-[#1a2d47]">
          <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
            <Users size={20} className="text-[#ff6b35]" />
            Homepage Statistics
          </h2>
          <p className="text-[#a8b8d0] text-sm mb-4">
            These statistics are displayed on the homepage. You can include "+" symbol (e.g., "500+").
          </p>
          <div className="space-y-4">
            <div>
              <label className="text-[#a8b8d0] text-sm mb-1 block flex items-center gap-2">
                <Users size={14} />
                Happy Customers
              </label>
              <Input 
                value={settings.stat_happy_customers || '500+'}
                onChange={(e) => handleChange('stat_happy_customers', e.target.value)}
                placeholder="500+"
                className="bg-[#1a2d47] border-[#1a4d8c] text-white" 
              />
            </div>
            <div>
              <label className="text-[#a8b8d0] text-sm mb-1 block flex items-center gap-2">
                <Car size={14} />
                Vehicles Sold
              </label>
              <Input 
                value={settings.stat_vehicles_sold || '100+'}
                onChange={(e) => handleChange('stat_vehicles_sold', e.target.value)}
                placeholder="100+"
                className="bg-[#1a2d47] border-[#1a4d8c] text-white" 
              />
            </div>
            <div>
              <label className="text-[#a8b8d0] text-sm mb-1 block flex items-center gap-2">
                <Calendar size={14} />
                Years in Business
              </label>
              <Input 
                value={settings.stat_years_in_business || '5'}
                onChange={(e) => handleChange('stat_years_in_business', e.target.value)}
                placeholder="5"
                className="bg-[#1a2d47] border-[#1a4d8c] text-white" 
              />
            </div>
          </div>
          
          {/* Preview */}
          <div className="mt-4 bg-[#1a4d8c]/30 rounded-lg p-4">
            <p className="text-[#a8b8d0] text-sm mb-3">Preview:</p>
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <p className="text-2xl font-bold text-white">{settings.stat_happy_customers || '500+'}</p>
                <p className="text-[#a8b8d0] text-xs">Happy Customers</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{settings.stat_vehicles_sold || '100+'}</p>
                <p className="text-[#a8b8d0] text-xs">Vehicles Sold</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{settings.stat_years_in_business || '5'}</p>
                <p className="text-[#a8b8d0] text-xs">Years in Business</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SiteSettingsManagement;
